import React, { useEffect, useMemo, useState } from 'react';
import { Table, Button, Space, Tag, message, Typography, Tooltip } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { letterService, Letter } from '../services/letters';
import { useTableColumnSearch } from '../hooks/useTableColumnSearch';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { ResizableTableHeader } from '../components/ResizableTableHeader';
import { ColumnVisibilityToggle } from '../components/ColumnVisibilityToggle';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import '../styles/resizable-table.css';

const { Title } = Typography;

type FilterType = 'all' | 'Plan' | 'Unread' | 'Read';

export const Letters: React.FC = () => {
  const [letters, setLetters] = useState<Letter[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canView = hasPermission(user, 'letters.view');
  const canCreate = hasPermission(user, 'letters.create');

  useEffect(() => {
    if (!canView) {
      setLetters([]);
      return;
    }
    loadLetters();
  }, [canView]);

  const loadLetters = async () => {
    if (!canView) {
      return;
    }
    setLoading(true);
    try {
      const response = await letterService.getAll();
      setLetters(response.data || response || []);
    } catch {
      message.error('Failed to load letters');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await letterService.delete(id);
      message.success('Letter deleted successfully');
      loadLetters();
    } catch {
      message.error('Failed to delete letter');
    }
  };

  const formatLetterNumber = (letterId: number) => {
    return `#${letterId.toString().padStart(7, '0')}`;
  };

  const getReferenceLabel = (record: Letter) => {
    if (record.type === 'business') {
      if (record.business?.name) return record.business.name;
      if (record.reference_id) return `Business #${record.reference_id}`;
      return '-';
    }
    if (record.type === 'personal') {
      if (record.customer?.name) return record.customer.name;
      if (record.reference_id) return `Customer #${record.reference_id}`;
      return '-';
    }
    return record.customer?.name || record.business?.name || '-';
  };

  const baseColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      sorter: (a: Letter, b: Letter) => a.id - b.id,
      render: (id: number) => formatLetterNumber(id),
    },
    {
      title: 'Customer',
      dataIndex: ['customer', 'name'],
      key: 'customer',
      width: 180,
      sorter: (a: Letter, b: Letter) => getReferenceLabel(a).localeCompare(getReferenceLabel(b)),
      render: (_: string, record: Letter) => (
        <Tooltip title={getReferenceLabel(record)} placement="topLeft">
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {getReferenceLabel(record)}
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Subject',
      dataIndex: ['subject', 'name'],
      key: 'subject',
      width: 200,
      sorter: (a: Letter, b: Letter) => (a.subject?.name || '').localeCompare(b.subject?.name || ''),
    },
    {
      title: 'Sender',
      dataIndex: ['sender', 'name'],
      key: 'sender',
      width: 160,
      sorter: (a: Letter, b: Letter) => (a.sender?.name || '').localeCompare(b.sender?.name || ''),
    },
    {
      title: 'Create Date',
      dataIndex: 'create_date',
      key: 'create_date',
      width: 120,
      sorter: (a: Letter, b: Letter) => {
        if (!a.create_date && !b.create_date) return 0;
        if (!a.create_date) return 1;
        if (!b.create_date) return -1;
        return new Date(a.create_date).getTime() - new Date(b.create_date).getTime();
      },
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Due Date',
      dataIndex: 'due_date',
      key: 'due_date',
      width: 120,
      sorter: (a: Letter, b: Letter) => {
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
      },
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      sorter: (a: Letter, b: Letter) => (a.status || 'Plan').localeCompare(b.status || 'Plan'),
      render: (status: string) => {
        // Map old status values to new ones
        const mapStatus = (s: string | undefined): string => {
          if (!s) return 'Plan';
          if (s === 'draft') return 'Plan';
          if (s === 'sent') return 'Unread';
          return s;
        };
        const mappedStatus = mapStatus(status);
        const statusConfig: Record<string, { color: string; label: string }> = {
          Plan: { color: 'blue', label: 'Plan' },
          Unread: { color: 'orange', label: 'Unread' },
          Read: { color: 'green', label: 'Read' },
        };
        const config = statusConfig[mappedStatus] || { color: 'blue', label: mappedStatus || 'Plan' };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
  ];

  const {
    columnConfig,
    visibleColumns,
    visibleKeys,
    handleColumnVisibilityChange,
  } = useColumnVisibility(baseColumns);

  const { columns: resizableColumns } = useResizableColumns(visibleColumns);

  const memoizedColumns = useMemo(
    () =>
      resizableColumns.map((col) => ({
        ...col,
        width: col.width, // Preserve width
        onHeaderCell: col.onHeaderCell, // Preserve onHeaderCell for resizing
        getSearchValue:
          col.dataIndex === 'id'
            ? (record: Letter) => formatLetterNumber(record.id)
            : Array.isArray(col.dataIndex) && col.dataIndex[0] === 'customer' && col.dataIndex[1] === 'name'
            ? (record: Letter) => getReferenceLabel(record)
            : Array.isArray(col.dataIndex) && col.dataIndex[0] === 'subject' && col.dataIndex[1] === 'name'
            ? (record: Letter) => record.subject?.name
            : Array.isArray(col.dataIndex) && col.dataIndex[0] === 'sender' && col.dataIndex[1] === 'name'
            ? (record: Letter) => record.sender?.name
            : col.dataIndex === 'create_date'
            ? (record: Letter) => record.create_date
            : col.dataIndex === 'due_date'
            ? (record: Letter) => record.due_date
            : col.dataIndex === 'status'
            ? (record: Letter) => {
                const statusConfig: Record<string, string> = {
                  Plan: 'Plan',
                  Unread: 'Unread',
                  Read: 'Read',
                };
                return statusConfig[record.status] || record.status || 'Plan';
              }
            : (undefined as unknown as (record: Letter) => string | number | null | undefined),
      })),
    [resizableColumns]
  );

  const filteredLetters = useMemo(() => {
    if (filter === 'all') {
      return letters;
    } else {
      return letters.filter(letter => {
        // Map old status values
        const mapStatus = (s: string | undefined): string => {
          if (!s) return 'Plan';
          if (s === 'draft') return 'Plan';
          if (s === 'sent') return 'Unread';
          return s;
        };
        const mappedStatus = mapStatus(letter.status);
        return mappedStatus === filter;
      });
    }
  }, [letters, filter]);

  const { columnsWithSearch, filteredData } = useTableColumnSearch<Letter>(filteredLetters, memoizedColumns as any);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          Letters
        </Title>
        <Space>
          <ColumnVisibilityToggle
            columns={columnConfig}
            visibleKeys={visibleKeys}
            onChange={handleColumnVisibilityChange}
          />
            {canCreate && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/letters/new')}
              >
                New Letter
              </Button>
            )}
        </Space>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button 
            type={filter === 'all' ? 'primary' : 'default'}
            onClick={() => setFilter('all')}
          >
            All
          </Button>
          <Button 
            type={filter === 'Plan' ? 'primary' : 'default'}
            onClick={() => setFilter('Plan')}
          >
            Plan
          </Button>
          <Button 
            type={filter === 'Unread' ? 'primary' : 'default'}
            onClick={() => setFilter('Unread')}
          >
            Unread
          </Button>
          <Button 
            type={filter === 'Read' ? 'primary' : 'default'}
            onClick={() => setFilter('Read')}
          >
            Read
          </Button>
        </Space>
      </div>

      <Table
        columns={columnsWithSearch}
        dataSource={filteredData}
        loading={loading}
        rowKey="id"
        pagination={false}
        scroll={{ y: 'calc(100vh - 400px)' }}
        components={{
          header: {
            cell: ResizableTableHeader,
          },
        }}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
          hideSelectAll: true,
          columnWidth: 0,
          renderCell: () => null,
        }}
        footer={() => (
          <div style={{ textAlign: 'left', padding: '8px 0' }}>
            Total: {filteredData.length} {filteredData.length === 1 ? 'letter' : 'letters'}
          </div>
        )}
        onRow={(record) => ({
          onClick: () => setSelectedRowKeys([record.id]),
          onDoubleClick: canView ? () => navigate(`/letters/${record.id}/edit`) : undefined,
          style: { cursor: canView ? 'pointer' : 'default' },
        })}
      />
    </div>
  );
};
